'use client';

import {
  useKeyboardShortcutsStore,
  type KeyboardShortcut,
} from './keyboard-shortcuts.store';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

// Get all shortcuts
export const useShortcuts = () => {
  // Use shallow comparison to prevent infinite loops
  return useKeyboardShortcutsStore(
    useShallow((state) => Array.from(state.shortcuts.values()))
  );
};

// Get enabled shortcuts only
export const useEnabledShortcuts = () => {
  return useKeyboardShortcutsStore(
    useShallow((state) =>
      Array.from(state.shortcuts.values()).filter((s) => s.enabled !== false)
    )
  );
};

// Get shortcuts by category
export const useShortcutsByCategory = (category: string) => {
  return useKeyboardShortcutsStore(
    useShallow((state) =>
      Array.from(state.shortcuts.values()).filter(
        (s) => s.category === category && s.enabled !== false
      )
    )
  );
};

// Get all categories
export const useShortcutCategories = () => {
  return useKeyboardShortcutsStore(
    useShallow((state) => Array.from(state.categories))
  );
};

// Get help panel visibility
export const useShowHelp = () => {
  return useKeyboardShortcutsStore((state) => state.showHelp);
};

// Get enabled state
export const useShortcutsEnabled = () => {
  return useKeyboardShortcutsStore((state) => state.isEnabled);
};

// Get grouped shortcuts by category
export const useGroupedShortcuts = () => {
  const shortcuts = useEnabledShortcuts();
  const categories = useShortcutCategories();

  return useMemo(() => {
    const grouped: Record<string, KeyboardShortcut[]> = {
      General: [],
    };

    categories.forEach((category) => {
      grouped[category] = [];
    });

    shortcuts.forEach((shortcut) => {
      const category = shortcut.category || 'General';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(shortcut);
    });

    // Remove empty categories
    Object.keys(grouped).forEach((key) => {
      const categoryShortcuts = grouped[key];
      if (categoryShortcuts && categoryShortcuts.length === 0) {
        delete grouped[key];
      }
    });

    return grouped;
  }, [shortcuts, categories]);
};

// Check if a specific shortcut is registered
export const useIsShortcutRegistered = (key: string) => {
  // Only return the boolean result, no array creation needed
  return useKeyboardShortcutsStore((state) => {
    for (const shortcut of state.shortcuts.values()) {
      if (shortcut.key === key) return true;
    }
    return false;
  });
};

// Actions
export const useShortcutActions = () => {
  const addShortcut = useKeyboardShortcutsStore((state) => state.addShortcut);
  const addShortcuts = useKeyboardShortcutsStore((state) => state.addShortcuts);
  const removeShortcut = useKeyboardShortcutsStore(
    (state) => state.removeShortcut
  );
  const removeShortcuts = useKeyboardShortcutsStore(
    (state) => state.removeShortcuts
  );
  const updateShortcut = useKeyboardShortcutsStore(
    (state) => state.updateShortcut
  );
  const toggleHelp = useKeyboardShortcutsStore((state) => state.toggleHelp);
  const showHelpPanel = useKeyboardShortcutsStore(
    (state) => state.showHelpPanel
  );
  const hideHelpPanel = useKeyboardShortcutsStore(
    (state) => state.hideHelpPanel
  );
  const setEnabled = useKeyboardShortcutsStore((state) => state.setEnabled);
  const clearAll = useKeyboardShortcutsStore((state) => state.clearAll);
  const executeShortcut = useKeyboardShortcutsStore(
    (state) => state.executeShortcut
  );

  return {
    addShortcut,
    addShortcuts,
    removeShortcut,
    removeShortcuts,
    updateShortcut,
    toggleHelp,
    showHelpPanel,
    hideHelpPanel,
    setEnabled,
    clearAll,
    executeShortcut,
  };
};
